import cn from "classnames";
import { Component, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { toast } from 'sonner';
import { AVAILABLE_LANGS } from "../../../utils/constants";
import Loader from "./../../../components/Loader";
import styles from "./Add.module.sass";
import TopicNameAndInfo from "./components/NameAndInfo";
import TopicColors from "./components/TopicColors";
import TopicContent from "./components/TopicContent";
import TopicDates from "./components/TopicDate";
import TopicImage from "./components/TopicImage";
import TopicPreview from "./components/TopicPreview";
import { createTopic, getTopicById, updateTopic } from "../../../api/topic.service";

// Error boundary for TopicContent so we can show "Try again" without reloading the page
class TopicContentErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error('TopicContent error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>Error loading content</h3>
          <p>There was an error loading the topic content. Please try again.</p>
          <button
            type="button"
            onClick={() => this.props.onRetry?.()}
            style={{ marginTop: '10px', padding: '10px 20px' }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const NewTopic = ({ isNew = false }) => {
    const { t } = useTranslation();

    // Estados del formulario - adaptados al DTO
    const [active, setActive] = useState(false); // Por defecto false
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [textColor, setTextColor] = useState("#FFFFFF");
    const [backgroundColor, setBackgroundColor] = useState("#1c1e22");
    // const [selectedDecks, setSelectedDecks] = useState([]); // Removed
    // const [selectedTexts, setSelectedTexts] = useState([]); // Removed
    const [selectedItems, setSelectedItems] = useState([]); // New Unified State
    const selectedItemsRef = useRef(selectedItems);
    selectedItemsRef.current = selectedItems;

    const [topicImageUrl, setTopicImageUrl] = useState('');
    const [file, setFile] = useState(null);
    const [fileURL, setFileURL] = useState('');

    // Estados de traducciones - ahora usando el formato del DTO
    const [title, setTitle] = useState(AVAILABLE_LANGS.map(lang => ({
        lang,
        trans: '',
        active: true
    })));

    const [description, setDescription] = useState(AVAILABLE_LANGS.map(lang => ({
        lang,
        trans: '',
        active: true
    })));

    // Estados de UI
    const [visiblePreview, setVisiblePreview] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTopic, setLoadingTopic] = useState(false);
    const [topicError, setTopicError] = useState(null);
    const [topicContentKey, setTopicContentKey] = useState(0);

    const history = useHistory();
    const { topicId } = useParams();

    // Cargar topic para edición
    // populateFormWithTopic uses local setters only; we keep effect tied to route/language changes.
    useEffect(() => {
        if (topicId && !isNew) {
            const loadTopicForEdit = async () => {
                setLoadingTopic(true);
                setTopicError(null);
                try {
                    console.log('Loading topic with ID:', topicId);
                    const response = await getTopicById(topicId);
                    console.log('Topic response:', response);

                    if (response.data) {
                        const topic = response.data; // El backend devuelve directamente el objeto
                        populateFormWithTopic(topic);
                    } else {
                        console.error('No topic data in response');
                        setTopicError('Topic not found');
                        toast.error(t('toast.topicNotFound'));
                    }
                } catch (error) {
                    console.error('Error loading topic:', error);
                    setTopicError('Error loading topic');
                    toast.error(t('toast.errorLoadingTopic'));
                } finally {
                    setLoadingTopic(false);
                }
            };

            loadTopicForEdit();
        }
    }, [isNew, topicId, t]); // eslint-disable-line react-hooks/exhaustive-deps

    // Limpiar formulario para nuevo topic
    useEffect(() => {
        if (isNew) {
            resetForm();
        }
    }, [isNew]);

    function populateFormWithTopic(topic) {
        try {
            console.log('Populating form with topic:', topic); // Debug

            // Configurar active - asegurar que se lee correctamente
            setActive(Boolean(topic.active));
            console.log('Setting active to:', Boolean(topic.active)); // Debug

            // Configurar fechas
            setStartDate(new Date(topic.startDate));
            setEndDate(new Date(topic.endDate));

            // Configurar colores
            setTextColor(topic.textColor);
            setBackgroundColor(topic.backgroundColor);

            // Configurar contenido
            // Prioritize topic.items (CatalogItems) if available
            // Fallback to decks/texts for migration compatibility
            let items = [];
            if (topic.catalogItems && Array.isArray(topic.catalogItems)) {
                items = topic.catalogItems.map(item => {
                    let titleStr = 'Untitled';
                    if (item.title && typeof item.title === 'string') {
                        titleStr = item.title;
                    } else if (item.translations && Array.isArray(item.translations)) {
                        const en = item.translations.find(t => t.lang === 'en');
                        const es = item.translations.find(t => t.lang === 'es');
                        titleStr = en?.trans || es?.trans || item.translations[0]?.trans || 'Untitled';
                    }
                    let imageUrl = item.imageUrl;
                    if (!imageUrl && item.imageSizes) {
                        imageUrl = item.imageSizes.medium || item.imageSizes.small || item.imageSizes.thumb;
                    }
                    // sourceId es el identificador en el catálogo (deck/text id); necesario para guardar
                    const sourceId = item.sourceId != null ? String(item.sourceId) : (item._id != null ? String(item._id) : null);
                    return {
                        ...item,
                        title: titleStr,
                        imageUrl: imageUrl,
                        sourceId,
                        type: item.type || 'deck',
                    };
                }).filter(item => item.sourceId);
            } else {
                // Combine decks and texts, marking their type
                const decks = Array.isArray(topic.decks) ? topic.decks.map(d => ({ ...d, type: 'deck' })) : [];
                const texts = Array.isArray(topic.texts) ? topic.texts.map(t => ({ ...t, type: 'text' })) : [];
                items = [...decks, ...texts];
            }
            setSelectedItems(items);

        // Configurar imagen - priorizar topicImageUrl de la API V3
        if (topic.topicImageUrl) {
            // URL firmada de CloudFront desde la API V3
            setTopicImageUrl(topic.topicImageUrl);
            setFileURL('');
            setFile(null);
        } else if (topic.fileImage) {
            // Fallback: manejar fileImage legacy
            if (topic.fileImage.startsWith('data:')) {
                // Es base64
                setFile(topic.fileImage);
                setFileURL(topic.fileImage);
            } else {
                // Es URL
                setTopicImageUrl(topic.fileImage);
                setFileURL('');
                setFile(null);
            }
        } else {
            setTopicImageUrl('');
            setFileURL('');
            setFile(null);
        }

        // Configurar traducciones para title
        if (topic.title && Array.isArray(topic.title)) {
            setTitle(topic.title);
        } else {
            // Si no hay traducciones, crear estructura vacía
            setTitle(AVAILABLE_LANGS.map(lang => ({
                lang,
                trans: '',
                active: true
            })));
        }

            // Configurar traducciones para description
            if (topic.description && Array.isArray(topic.description)) {
                setDescription(topic.description);
            } else {
                // Si no hay traducciones, crear estructura vacía
                setDescription(AVAILABLE_LANGS.map(lang => ({
                    lang,
                    trans: '',
                    active: true
                })));
            }
        } catch (error) {
            console.error('Error populating form with topic:', error);
            console.error('Topic data that caused error:', topic);
            toast.error(t('toast.errorLoadingTopicData'));
            
            // Fallback: limpiar todos los estados para evitar errores
            setSelectedItems([]);
            setActive(false);
            setStartDate(new Date());
            setEndDate(new Date());
            setTextColor('');
            setBackgroundColor('');
            setTitle(AVAILABLE_LANGS.map(lang => ({
                lang,
                trans: '',
                active: lang === 'en'
            })));
            setDescription(AVAILABLE_LANGS.map(lang => ({
                lang,
                trans: '',
                active: lang === 'en'
            })));
        }
    }

    const resetForm = () => {
        setActive(false); // Por defecto false
        setStartDate(new Date());
        setEndDate(new Date());
        setTextColor("#FFFFFF");
        setBackgroundColor("#1c1e22");
        setSelectedItems([]);
        setTopicImageUrl("");
        setFile(null);
        setFileURL('');

        // Resetear traducciones
        setTitle(AVAILABLE_LANGS.map(lang => ({
            lang,
            trans: '',
            active: true
        })));

        setDescription(AVAILABLE_LANGS.map(lang => ({
            lang,
            trans: '',
            active: true
        })));
    };

    const handleSave = async () => {
        setIsLoading(true);

        // Construir el objeto según el DTO
        const postData = {
            active,
            title, // Array de TranslationDto
            description, // Array de TranslationDto
            startDate,
            endDate,
            textColor,
            backgroundColor,
        };

        // Array de IDs de los ítems del catálogo seleccionados (sourceIds)
        // Usar ref para garantizar siempre el estado más reciente al guardar
        const itemsToSave = selectedItemsRef.current;
        const ids = itemsToSave
            .map(item => {
                const id = item.sourceId ?? item._id ?? item.id;
                if (id) return typeof id === 'string' ? id : String(id);
                // Fallback: catalogRef es "deck:xxx" o "text:xxx"
                const ref = item.catalogRef;
                if (typeof ref === 'string' && ref.includes(':')) return ref.split(':')[1];
                return null;
            })
            .filter(Boolean);
        postData.catalogItems = ids;
        postData.decks = [];
        postData.texts = [];

        // Debug payload
        console.log('Saving Topic Payload:', postData);

        // Agregar imagen como base64 si existe
        if (file) {
            postData.fileImage = file; // Ya es base64
        }

        try {
            let response;
            if (topicId && !isNew) {
                postData._id = topicId;
                response = await updateTopic(postData);
            } else {
                response = await createTopic(postData);
            }

            if (response?.data) {
                toast.success(isNew ? t('toast.topicCreated') : t('toast.topicUpdated'));
                setTimeout(() => {
                    history.push('/topics');
                }, 1000);
            } else {
                throw new Error('Invalid response');
            }
        } catch (error) {
            console.error('Error saving topic:', error);
            toast.error(t('toast.errorSavingTopic'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSetFile = (e) => {
        const selectedFile = e?.target?.files?.[0];
        if (selectedFile) {
            // Convertir archivo a base64
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setFile(base64String); // Guardar como base64
                setFileURL(URL.createObjectURL(selectedFile)); // Para preview
            };
            reader.readAsDataURL(selectedFile);
        } else {
            setFile(null);
            setFileURL('');
        }
    };

    // Función para actualizar traducciones
    const updateTranslation = (field, lang, value) => {
        if (field === 'title') {
            setTitle(prev => prev.map(item =>
                item.lang === lang ? { ...item, trans: value } : item
            ));
        } else if (field === 'description') {
            setDescription(prev => prev.map(item =>
                item.lang === lang ? { ...item, trans: value } : item
            ));
        }
    };

    // Función para obtener traducción
    const getTranslation = (field, lang) => {
        if (field === 'title') {
            const translation = title.find(item => item.lang === lang);
            return translation && translation.trans ? translation.trans : '';
        } else if (field === 'description') {
            const translation = description.find(item => item.lang === lang);
            return translation && translation.trans ? translation.trans : '';
        }
        return '';
    };

    // Mostrar loading mientras se carga el tópico
    if (loadingTopic) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Loader />
                <span style={{ marginLeft: '10px' }}>Loading topic...</span>
            </div>
        );
    }

    // Mostrar error si hay problema cargando el tópico
    if (topicError) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
                <h3>Error loading topic</h3>
                <p>{topicError}</p>
                <button onClick={() => history.push('/topics')} style={{ marginTop: '20px', padding: '10px 20px' }}>
                    Back to Topics
                </button>
            </div>
        );
    }

    return (
        <>
            <div className={styles.row}>
                <div className={styles.col}>
                    {/* Name & Info */}
                    <TopicNameAndInfo
                        className={styles.card}
                        active={active}
                        setActive={setActive}
                        title={title}
                        setTitle={setTitle}
                        description={description}
                        setDescription={setDescription}
                        updateTranslation={updateTranslation}
                        getTranslation={getTranslation}
                    />

                    {/* Dates */}
                    <TopicDates
                        className={styles.card}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />

                    {/* Colors */}
                    <TopicColors
                        className={styles.card}
                        textColor={textColor}
                        setTextColor={setTextColor}
                        backgroundColor={backgroundColor}
                        setBackgroundColor={setBackgroundColor}
                    />

                    {/* Topic Image */}
                    <TopicImage
                        className={styles.card}
                        handleUploadFile={handleSetFile}
                        imageUrl={topicImageUrl}
                        fileURL={fileURL}
                    />

                    {/* Topic Content (Catalog Items) */}
                    <TopicContentErrorBoundary
                        key={topicContentKey}
                        onRetry={() => setTopicContentKey((k) => k + 1)}
                    >
                        <TopicContent
                            className={styles.card}
                            selectedItems={selectedItems}
                            setSelectedItems={setSelectedItems}
                        />
                    </TopicContentErrorBoundary>
                </div>

                <div className={styles.col}>
                    {/* Preview */}
                    <TopicPreview
                        visible={visiblePreview}
                        onClose={() => setVisiblePreview(false)}
                        title={getTranslation('title', 'en')}
                        description={getTranslation('description', 'en')}
                        textColor={textColor}
                        backgroundColor={backgroundColor}
                        imageUrl={fileURL || topicImageUrl}
                        // Need to update preview to handle items mixed? 
                        // Assuming TopicPreview can handle items or we map them back to decks/texts just for preview?
                        // For now we pass selectedItems as selectedDecks to avoid breaking?
                        // TopicPreview likely expects separation.
                        // I'll just pass selectedItems as both or update TopicPreview later if needed.
                        selectedDecks={selectedItems.filter(i => i.type === 'deck')}
                        selectedTexts={selectedItems.filter(i => i.type === 'text')}
                        startDate={startDate}
                        endDate={endDate}
                        active={active}
                        translations={{ title, description }}
                    />
                </div>
            </div>

            {/* Save Button */}
            <div className={cn("button", styles.button)} onClick={handleSave}>
                {isLoading ? (
                    <Loader className={styles.loader} />
                ) : (
                    <span>{isNew ? t('topics.createTopic') : t('topics.saveChanges')}</span>
                )}
            </div>
        </>
    );
};

export default NewTopic;
